import { draftsStartingWith, removeDraft } from "../../hooks/useDraft";
import { scholarshipsApi } from "../../api/scholarships";
import { detailsBodyOf, toDetailsForm, validateDetails } from "./DetailsTab";
import { requirementSpecOf, validateRequirements } from "./DocumentsTab";
import { eligibilityRulesOf, validateEligibility } from "./EligibilityTab";
import { criteriaOf, validateCriteria } from "./CriteriaTab";

const hasErrors = (errors) => Object.keys(errors).length > 0;

// The unsaved changes of each set-up tab, as { key, label, invalid, request(etag) }; a part no longer offered is dropped
const pendingChangesOf = (scholarship) => {
  const { id } = scholarship;
  const programmes = scholarship.programmes ?? [];
  const levels = (scholarship.levels ?? []).map((l) => l.level);
  const programmeOf = (key, prefix) => programmes.find((p) => p.id === key.slice(prefix.length));
  const pending = [];
  const stale = [];

  const details = draftsStartingWith(`scholarship-edit:${id}`).find((d) => d.key === `scholarship-edit:${id}`);
  if (details) {
    const original = toDetailsForm(scholarship);
    const values = { ...original, reason: "", ...details.value };
    const changed = Object.keys(original).filter((name) => name in details.value && details.value[name] !== original[name]);
    if (changed.length)
      pending.push({
        key: details.key,
        label: "Details",
        invalid: hasErrors(validateDetails(values, original, scholarship)),
        request: (etag) => scholarshipsApi.patch(`admin/scholarships/${id}`, detailsBodyOf(values, changed), { ifMatch: etag }),
      });
    else stale.push(details.key);
  }

  const documentsPrefix = `scholarship-documents:${id}:`;
  draftsStartingWith(documentsPrefix).forEach(({ key, value }) => {
    const programme = programmeOf(key, documentsPrefix);
    if (!programme || !value?.documents) return stale.push(key);
    pending.push({
      key,
      label: `Documents of ${programme.name}`,
      invalid: hasErrors(validateRequirements(value.documents)),
      request: (etag) => scholarshipsApi.put(`admin/scholarships/${id}/programmes/${programme.id}/requirements`, { requirements: value.documents.map(requirementSpecOf) }, { ifMatch: etag }),
    });
  });

  const eligibilityPrefix = `scholarship-eligibility:${id}:level:`;
  draftsStartingWith(eligibilityPrefix).forEach(({ key, value }) => {
    const level = levels.find((l) => l.id === key.slice(eligibilityPrefix.length));
    if (!level || !value?.form) return stale.push(key);
    pending.push({
      key,
      label: `Eligibility for ${level.name ?? "a level"}`,
      invalid: hasErrors(validateEligibility(value.form)),
      request: (etag) => scholarshipsApi.put(`admin/scholarships/${id}/levels/${level.id}/eligibility`, { rules: eligibilityRulesOf(value.form) }, { ifMatch: etag }),
    });
  });

  const rubricPrefix = `scholarship-rubric:${id}:`;
  draftsStartingWith(rubricPrefix).forEach(({ key, value }) => {
    const programme = programmeOf(key, rubricPrefix);
    if (!programme || !value?.rows || programme.rubricLocked) return stale.push(key);
    pending.push({
      key,
      label: `Criteria of ${programme.name}`,
      invalid: hasErrors(validateCriteria(value.rows)),
      request: (etag) => scholarshipsApi.put(`admin/scholarships/${id}/programmes/${programme.id}/rubric`, { criteria: criteriaOf(value.rows) }, { ifMatch: etag }),
    });
  });

  return { pending, stale };
};

/**
 * Saves every set-up tab's unsaved changes of a draft, one after another (each save moves the
 * version on). A change that does not pass its tab's checks, or that the API refuses, stays as a
 * draft for its tab to show. Returns { saved, skipped, failed } as lists of labels.
 */
export const saveDrafts = async (scholarship, change) => {
  const { pending, stale } = pendingChangesOf(scholarship);
  stale.forEach(removeDraft);
  const result = { saved: [], skipped: [], failed: [] };
  for (const part of pending) {
    if (part.invalid) {
      result.skipped.push(part.label);
      continue;
    }
    try {
      await change(part.request);
      removeDraft(part.key);
      result.saved.push(part.label);
    } catch (error) {
      console.error(`Failed to save "${part.label}" of scholarship ${scholarship.id}:`, error);
      result.failed.push(part.label);
    }
  }
  return result;
};
